import type { PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import {
  centeredPopupFeatures,
  definePaymentMethod,
  type FraudMetadataWire,
  getOrCreateCheckoutRequestId,
  toFraudMetadataWire,
} from "../utils";

// PayPal-specific types
export type PayPalCustomerInfo = {
  first_name: string;
  last_name: string;
};

export type PayPalStartRequest = {
  processor_id: string;
  customer_info: PayPalCustomerInfo;
  fraud_metadata: FraudMetadataWire;
};

export type PayPalStartResponse = {
  approval_url: string;
  popup_token: string;
  checkout_attempt_id: string;
};

export type PayPalStatusResponse = {
  completed: boolean;
  status: "success" | "failed" | "cancelled";
  result?: unknown;
  error?: string;
};

export type PayPalCheckoutResponse = {
  id: string;
  checkoutAttemptId: string;
  checkoutSessionId: string;
  state: string;
  customerId?: string;
  paymentMethodId?: string;
  processorUsed?: string;
  subscriptionId?: string;
  invoiceId?: string;
  invoiceNumber?: number;
};

export type PayPalSubmitOptions = {
  processorId: string;
  customerInfo: PayPalCustomerInfo;
};

type PayPalStates = PaymentKitStates & {
  popup?: Window | null;
  pollInterval?: number;
};

const defSubmitPayment = (states: PayPalStates) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (_fields, options) => {
    const { apiBaseUrl, secureToken, environment } = states;
    const paypalOptions = options as PayPalSubmitOptions;

    if (!paypalOptions?.processorId) {
      return { errors: { processor_id: "Processor ID is required" } as PaymentKitErrors };
    }

    if (!paypalOptions?.customerInfo?.first_name || !paypalOptions?.customerInfo?.last_name) {
      return {
        errors: {
          customer_name: "Customer first and last name are required",
        } as PaymentKitErrors,
      };
    }

    try {
      // Get or create checkout request ID for correlating all API calls
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      console.log(`[PayPal] Using checkout_request_id: ${checkoutRequestId}`);

      // Step 1: Call PayPal start endpoint
      const startResponse = await fetch(`${apiBaseUrl}/api/checkout/${secureToken}/paypal/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-request-id": checkoutRequestId,
        },
        body: JSON.stringify({
          processor_id: paypalOptions.processorId,
          customer_info: paypalOptions.customerInfo,
          fraud_metadata: toFraudMetadataWire(checkoutRequestId),
        } as PayPalStartRequest),
      });

      if (!startResponse.ok) {
        let errorMessage = `Failed to start PayPal checkout (${startResponse.status})`;
        try {
          const errorData = await startResponse.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Response body is not JSON, use status text
          errorMessage = startResponse.statusText || errorMessage;
        }
        return {
          errors: {
            paypal: errorMessage,
          } as PaymentKitErrors,
        };
      }

      const { approval_url, popup_token, checkout_attempt_id } = (await startResponse.json()) as PayPalStartResponse;

      // Step 2: Open PayPal popup, centered over the browser window.
      const popup = window.open(approval_url, "PayPal", centeredPopupFeatures());

      if (!popup) {
        return {
          errors: {
            paypal: "Failed to open PayPal popup. Please allow popups for this site.",
          } as PaymentKitErrors,
        };
      }

      states.popup = popup;

      // Step 3: Poll for completion
      return new Promise((resolve) => {
        const pollInterval = setInterval(async () => {
          try {
            // Check status FIRST - popup might close after successful callback
            const statusResponse = await fetch(
              `${apiBaseUrl}/api/checkout/${secureToken}/popup/status?popup_token=${popup_token}`,
            );

            if (!statusResponse.ok) {
              clearInterval(pollInterval);
              popup.close();
              resolve({
                errors: {
                  paypal: "Failed to check PayPal status",
                } as PaymentKitErrors,
              });
              return;
            }

            const statusResult = (await statusResponse.json()) as PayPalStatusResponse;

            if (statusResult.completed) {
              clearInterval(pollInterval);
              popup.close();

              if (statusResult.status === "success") {
                // Result is returned directly from popup status endpoint
                const result = statusResult.result || {
                  id: checkout_attempt_id,
                  checkoutAttemptId: checkout_attempt_id,
                  checkoutSessionId: secureToken,
                  state: "checkout_succeeded",
                };

                resolve({ data: result });
              } else {
                resolve({
                  errors: {
                    paypal: statusResult.error || `PayPal checkout ${statusResult.status}`,
                  } as PaymentKitErrors,
                });
              }
              return;
            }

            // Only check popup.closed AFTER confirming status is not completed
            // This avoids race condition where popup closes from successful callback
            if (popup.closed) {
              clearInterval(pollInterval);
              resolve({
                errors: {
                  paypal: "PayPal popup closed by user",
                } as PaymentKitErrors,
              });
              return;
            }
          } catch (error) {
            clearInterval(pollInterval);
            popup.close();
            resolve({
              errors: {
                paypal: `Polling error: ${error}`,
              } as PaymentKitErrors,
            });
          }
        }, 2000); // Poll every 2 seconds

        states.pollInterval = pollInterval as unknown as number;
      });
    } catch (error) {
      return {
        errors: {
          paypal: `PayPal checkout error: ${error}`,
        } as PaymentKitErrors,
      };
    }
  };

  return submitPayment;
};

const PayPalPaymentMethod = definePaymentMethod((paymentKitStates) => {
  const localStates: PayPalStates = { ...paymentKitStates };

  return {
    name: "paypal",
    externalFuncs: {
      // PayPal doesn't need createElement like card iframes
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(localStates),
      cleanup: () => {
        // Clean up popup and polling interval if still active
        if (localStates.pollInterval) {
          clearInterval(localStates.pollInterval);
        }
        if (localStates.popup && !localStates.popup.closed) {
          localStates.popup.close();
        }
      },
    },
  };
});

export default PayPalPaymentMethod;
